import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  Edit,
  Trash2,
  Plus,
  X,
  Save,
  Loader2,
  AlertTriangle,
  ArrowRightLeft,
  MapPin,
  Link2,
  ExternalLink,
  Radio,
  Building2,
  Upload,
  Mail,
  Eye,
} from "lucide-react";
import { Layout } from "../../layouts/DashboardLayout";
import useCountryTransferts from "../../hooks/countryTransferts/useCountryTransferts";
import useCountries from "../../hooks/countries/useCountries";
import useToken from "../../../../hooks/auth/useToken";
import {
  CountryTransfert,
  CountryTransfertItem,
  CountryTransfertOperator,
  CountryTransfertBank,
  CountryTransfertEmail,
  Country,
} from "../../utils/database";
import { countryTransfertService } from "../../services/countryTransfertService";
import CountrySendersTab from "./countrySenders/CountrySendersTab";
import CorridorTransactionsModal from "./countryTransferts/CorridorTransactionsModal";
import DownloadRatesPdfButton from "../../components/shared/rates/DownloadRatesPdfButton";

type PageTab = "rates" | "corridors";

// Modal de gestion des éléments (items) d'un transfert par pays
const ItemsModal: React.FC<{
  transfert: CountryTransfert | null;
  isOpen: boolean;
  onClose: () => void;
}> = ({ transfert, isOpen, onClose }) => {
  const { token } = useToken();
  const [items, setItems] = useState<CountryTransfertItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState<CountryTransfertItem | null>(null);
  const [isCreatingItem, setIsCreatingItem] = useState(false);
  const [form, setForm] = useState({ name: "", url: "" });
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    if (!transfert?.id) return;
    setLoading(true);
    try {
      const data = await countryTransfertService.getCountryTransfertItems(Number(transfert.id));
      setItems(data || []);
    } catch (error) {
      console.error("Erreur lors du chargement des éléments:", error);
    } finally {
      setLoading(false);
    }
  }, [transfert?.id]);

  useEffect(() => {
    if (isOpen && transfert?.id) {
      fetchItems();
      setEditingItem(null);
      setIsCreatingItem(false);
      setForm({ name: "", url: "" });
    }
  }, [isOpen, transfert?.id, fetchItems]);

  const handleSave = async () => {
    if (!transfert?.id || !form.name.trim()) return;
    setSaving(true);
    try {
      if (isCreatingItem) {
        await countryTransfertService.createCountryTransfertItem(token, {
          country_transfert_id: Number(transfert.id),
          name: form.name.trim(),
          url: form.url.trim() || undefined,
        });
      } else if (editingItem?.id) {
        await countryTransfertService.updateCountryTransfertItem(token, editingItem.id, {
          name: form.name.trim(),
          url: form.url.trim() || undefined,
        });
      }
      setForm({ name: "", url: "" });
      setEditingItem(null);
      setIsCreatingItem(false);
      fetchItems();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      alert("Erreur lors de la sauvegarde de l'élément");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (itemId: number) => {
    if (!confirm("Supprimer cet élément ?")) return;
    try {
      await countryTransfertService.deleteCountryTransfertItem(token, itemId);
      fetchItems();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      alert("Erreur lors de la suppression");
    }
  };

  const startEdit = (item: CountryTransfertItem) => {
    setEditingItem(item);
    setIsCreatingItem(false);
    setForm({ name: item.name, url: item.url || "" });
  };

  const startCreate = () => {
    setIsCreatingItem(true);
    setEditingItem(null);
    setForm({ name: "", url: "" });
  };

  const cancelForm = () => {
    setEditingItem(null);
    setIsCreatingItem(false);
    setForm({ name: "", url: "" });
  };

  if (!isOpen || !transfert) return null;

  const senderName = transfert.country_sender?.name || "?";
  const destName = transfert.country_destination?.name || "?";
  const isFormActive = isCreatingItem || editingItem !== null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Éléments du transfert</h2>
            <p className="text-sm text-gray-500">
              {senderName} → {destName} (taux: {transfert.rate})
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        {isFormActive && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              {isCreatingItem ? "Nouvel élément" : "Modifier l'élément"}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Ex: Western Union, MoneyGram..."
                  disabled={saving}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                <input
                  type="text"
                  value={form.url}
                  onChange={(e) => setForm((prev) => ({ ...prev, url: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="https://..."
                  disabled={saving}
                />
              </div>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={cancelForm}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !form.name.trim()}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 text-sm disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  <span>{isCreatingItem ? "Créer" : "Modifier"}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add button */}
        {!isFormActive && (
          <button
            onClick={startCreate}
            className="mb-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter un élément</span>
          </button>
        )}

        {/* Items list */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-green-600" />
          </div>
        ) : items.length > 0 ? (
          <div className="space-y-2">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800 truncate">{item.name}</p>
                  {item.url && (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1 truncate"
                    >
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{item.url}</span>
                    </a>
                  )}
                </div>
                <div className="flex items-center space-x-1 ml-2">
                  <button
                    onClick={() => startEdit(item)}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Modifier"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id!)}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500 py-8">Aucun élément pour ce transfert</p>
        )}
      </div>
    </div>
  );
};

// Modal de gestion des emails d'un transfert par pays
const EmailsModal: React.FC<{
  transfert: CountryTransfert | null;
  isOpen: boolean;
  onClose: () => void;
}> = ({ transfert, isOpen, onClose }) => {
  const { token } = useToken();
  const [emails, setEmails] = useState<CountryTransfertEmail[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState<CountryTransfertEmail | null>(null);
  const [isCreatingItem, setIsCreatingItem] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [saving, setSaving] = useState(false);

  const fetchEmails = useCallback(async () => {
    if (!transfert?.id) return;
    setLoading(true);
    try {
      const data = await countryTransfertService.getCountryTransfertEmails(Number(transfert.id));
      setEmails(data || []);
    } catch (error) {
      console.error("Erreur lors du chargement des emails:", error);
    } finally {
      setLoading(false);
    }
  }, [transfert?.id]);

  useEffect(() => {
    if (isOpen && transfert?.id) {
      fetchEmails();
      setEditingItem(null);
      setIsCreatingItem(false);
      setForm({ name: "", email: "" });
    }
  }, [isOpen, transfert?.id, fetchEmails]);

  const handleSave = async () => {
    if (!transfert?.id || !form.name.trim() || !form.email.trim()) return;
    setSaving(true);
    try {
      if (isCreatingItem) {
        await countryTransfertService.createCountryTransfertEmail(token, {
          country_transfert_id: Number(transfert.id),
          name: form.name.trim(),
          email: form.email.trim(),
        });
      } else if (editingItem?.id) {
        await countryTransfertService.updateCountryTransfertEmail(token, editingItem.id, {
          name: form.name.trim(),
          email: form.email.trim(),
        });
      }
      setForm({ name: "", email: "" });
      setEditingItem(null);
      setIsCreatingItem(false);
      fetchEmails();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      alert("Erreur lors de la sauvegarde de l'email");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (emailId: string) => {
    if (!confirm("Supprimer cette boite email ?")) return;
    try {
      await countryTransfertService.deleteCountryTransfertEmail(token, emailId);
      fetchEmails();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      alert("Erreur lors de la suppression");
    }
  };

  const startEdit = (item: CountryTransfertEmail) => {
    setEditingItem(item);
    setIsCreatingItem(false);
    setForm({ name: item.name, email: item.email });
  };

  const startCreate = () => {
    setIsCreatingItem(true);
    setEditingItem(null);
    setForm({ name: "", email: "" });
  };

  const cancelForm = () => {
    setEditingItem(null);
    setIsCreatingItem(false);
    setForm({ name: "", email: "" });
  };

  if (!isOpen || !transfert) return null;

  const senderName = transfert.country_sender?.name || "?";
  const destName = transfert.country_destination?.name || "?";
  const isFormActive = isCreatingItem || editingItem !== null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Emails du transfert</h2>
            <p className="text-sm text-gray-500">
              {senderName} → {destName} (taux: {transfert.rate})
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {isFormActive && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              {isCreatingItem ? "Nouvelle boite email" : "Modifier la boite email"}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Ex: Support, Notifications, Depot Interac..."
                  disabled={saving}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="contact@example.com"
                  disabled={saving}
                />
              </div>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={cancelForm}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !form.name.trim() || !form.email.trim()}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 text-sm disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  <span>{isCreatingItem ? "Créer" : "Modifier"}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {!isFormActive && (
          <button
            onClick={startCreate}
            className="mb-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter un email</span>
          </button>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-green-600" />
          </div>
        ) : emails.length > 0 ? (
          <div className="space-y-2">
            {emails.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800 truncate">{item.name}</p>
                  <a
                    href={`mailto:${item.email}`}
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1 truncate"
                  >
                    <Mail className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{item.email}</span>
                  </a>
                </div>
                <div className="flex items-center space-x-1 ml-2">
                  <button
                    onClick={() => startEdit(item)}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Modifier"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => item.id && handleDelete(item.id)}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500 py-8">Aucun email configure pour ce transfert</p>
        )}
      </div>
    </div>
  );
};

// Modal de gestion des opérateurs d'un transfert par pays
const OperatorsModal: React.FC<{
  transfert: CountryTransfert | null;
  isOpen: boolean;
  onClose: () => void;
}> = ({ transfert, isOpen, onClose }) => {
  const { token } = useToken();
  const [operators, setOperators] = useState<CountryTransfertOperator[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState<CountryTransfertOperator | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState({ name: "", status: "ACTIVE" });
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchOperators = useCallback(async () => {
    if (!transfert?.id) return;
    setLoading(true);
    try {
      const data = await countryTransfertService.getCountryTransfertOperators(Number(transfert.id));
      setOperators(data || []);
    } catch (error) {
      console.error("Erreur lors du chargement des opérateurs:", error);
    } finally {
      setLoading(false);
    }
  }, [transfert?.id]);

  useEffect(() => {
    if (isOpen && transfert?.id) {
      fetchOperators();
      setEditingItem(null);
      setIsCreating(false);
      setForm({ name: "", status: "ACTIVE" });
      setFile(null);
    }
  }, [isOpen, transfert?.id, fetchOperators]);

  const handleSave = async () => {
    if (!transfert?.id || !form.name.trim()) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("country_transfert_id", String(transfert.id));
      formData.append("name", form.name.trim());
      formData.append("status", form.status);
      if (file) formData.append("file", file);

      if (isCreating) {
        await countryTransfertService.createCountryTransfertOperator(token, formData);
      } else if (editingItem?.id) {
        const updateData = new FormData();
        updateData.append("name", form.name.trim());
        updateData.append("status", form.status);
        if (file) updateData.append("file", file);
        await countryTransfertService.updateCountryTransfertOperator(token, editingItem.id, updateData);
      }
      setForm({ name: "", status: "ACTIVE" });
      setFile(null);
      setEditingItem(null);
      setIsCreating(false);
      fetchOperators();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      alert("Erreur lors de la sauvegarde de l'opérateur");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Supprimer cet opérateur ?")) return;
    try {
      await countryTransfertService.deleteCountryTransfertOperator(token, id);
      fetchOperators();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      alert("Erreur lors de la suppression");
    }
  };

  const startEdit = (item: CountryTransfertOperator) => {
    setEditingItem(item);
    setIsCreating(false);
    setForm({ name: item.name, status: item.status });
    setFile(null);
  };

  const startCreate = () => {
    setIsCreating(true);
    setEditingItem(null);
    setForm({ name: "", status: "ACTIVE" });
    setFile(null);
  };

  const cancelForm = () => {
    setEditingItem(null);
    setIsCreating(false);
    setForm({ name: "", status: "ACTIVE" });
    setFile(null);
  };

  if (!isOpen || !transfert) return null;

  const senderName = transfert.country_sender?.name || "?";
  const destName = transfert.country_destination?.name || "?";
  const isFormActive = isCreating || editingItem !== null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Opérateurs du transfert</h2>
            <p className="text-sm text-gray-500">
              {senderName} → {destName} (taux: {transfert.rate})
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {isFormActive && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              {isCreating ? "Nouvel opérateur" : "Modifier l'opérateur"}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Ex: Orange, MTN, Moov, Wave..."
                  disabled={saving}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={saving}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fichier (logo)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={saving}
                />
                {editingItem?.file_url && !file && (
                  <p className="text-xs text-gray-500 mt-1">Fichier actuel: {editingItem.file}</p>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={cancelForm}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !form.name.trim()}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 text-sm disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  <span>{isCreating ? "Créer" : "Modifier"}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {!isFormActive && (
          <button
            onClick={startCreate}
            className="mb-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter un opérateur</span>
          </button>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-green-600" />
          </div>
        ) : operators.length > 0 ? (
          <div className="space-y-2">
            {operators.map((op) => (
              <div
                key={op.id}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {op.file_url ? (
                    <img src={op.file_url} alt={op.name} className="w-8 h-8 object-contain rounded" />
                  ) : (
                    <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                      <Radio className="w-4 h-4 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">{op.name}</p>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${op.status === "ACTIVE" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}
                    >
                      {op.status}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-1 ml-2">
                  <button
                    onClick={() => startEdit(op)}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Modifier"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(op.id!)}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500 py-8">Aucun opérateur pour ce transfert</p>
        )}
      </div>
    </div>
  );
};

// Modal de gestion des banques d'un transfert par pays
const BanksModal: React.FC<{
  transfert: CountryTransfert | null;
  isOpen: boolean;
  onClose: () => void;
}> = ({ transfert, isOpen, onClose }) => {
  const { token } = useToken();
  const [banks, setBanks] = useState<CountryTransfertBank[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState<CountryTransfertBank | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState({ code: "", bank_name: "", status: "ACTIVE" });
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchBanks = useCallback(async () => {
    if (!transfert?.id) return;
    setLoading(true);
    try {
      const data = await countryTransfertService.getCountryTransfertBanks(Number(transfert.id));
      setBanks(data || []);
    } catch (error) {
      console.error("Erreur lors du chargement des banques:", error);
    } finally {
      setLoading(false);
    }
  }, [transfert?.id]);

  useEffect(() => {
    if (isOpen && transfert?.id) {
      fetchBanks();
      setEditingItem(null);
      setIsCreating(false);
      setForm({ code: "", bank_name: "", status: "ACTIVE" });
      setFile(null);
    }
  }, [isOpen, transfert?.id, fetchBanks]);

  const handleSave = async () => {
    if (!transfert?.id || !form.code.trim() || !form.bank_name.trim()) return;
    setSaving(true);
    try {
      if (isCreating) {
        const formData = new FormData();
        formData.append("country_transfert_id", String(transfert.id));
        formData.append("code", form.code.trim());
        formData.append("bank_name", form.bank_name.trim());
        formData.append("status", form.status);
        if (file) formData.append("file", file);
        await countryTransfertService.createCountryTransfertBank(token, formData);
      } else if (editingItem?.id) {
        const updateData = new FormData();
        updateData.append("code", form.code.trim());
        updateData.append("bank_name", form.bank_name.trim());
        updateData.append("status", form.status);
        if (file) updateData.append("file", file);
        await countryTransfertService.updateCountryTransfertBank(token, editingItem.id, updateData);
      }
      setForm({ code: "", bank_name: "", status: "ACTIVE" });
      setFile(null);
      setEditingItem(null);
      setIsCreating(false);
      fetchBanks();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      alert("Erreur lors de la sauvegarde de la banque");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Supprimer cette banque ?")) return;
    try {
      await countryTransfertService.deleteCountryTransfertBank(token, id);
      fetchBanks();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      alert("Erreur lors de la suppression");
    }
  };

  const startEdit = (item: CountryTransfertBank) => {
    setEditingItem(item);
    setIsCreating(false);
    setForm({ code: item.code, bank_name: item.bank_name, status: item.status });
    setFile(null);
  };

  const startCreate = () => {
    setIsCreating(true);
    setEditingItem(null);
    setForm({ code: "", bank_name: "", status: "ACTIVE" });
    setFile(null);
  };

  const cancelForm = () => {
    setEditingItem(null);
    setIsCreating(false);
    setForm({ code: "", bank_name: "", status: "ACTIVE" });
    setFile(null);
  };

  if (!isOpen || !transfert) return null;

  const senderName = transfert.country_sender?.name || "?";
  const destName = transfert.country_destination?.name || "?";
  const isFormActive = isCreating || editingItem !== null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Banques du transfert</h2>
            <p className="text-sm text-gray-500">
              {senderName} → {destName} (taux: {transfert.rate})
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {isFormActive && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              {isCreating ? "Nouvelle banque" : "Modifier la banque"}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Ex: 10001, ECOCIBJ..."
                  disabled={saving}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la banque *</label>
                <input
                  type="text"
                  value={form.bank_name}
                  onChange={(e) => setForm((prev) => ({ ...prev, bank_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Ex: Ecobank, BNI, SGCI..."
                  disabled={saving}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={saving}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo (fichier)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={saving}
                />
                {editingItem?.file_url && !file && (
                  <p className="text-xs text-gray-500 mt-1">Fichier actuel: {editingItem.file}</p>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={cancelForm}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !form.code.trim() || !form.bank_name.trim()}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 text-sm disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  <span>{isCreating ? "Créer" : "Modifier"}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {!isFormActive && (
          <button
            onClick={startCreate}
            className="mb-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter une banque</span>
          </button>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-green-600" />
          </div>
        ) : banks.length > 0 ? (
          <div className="space-y-2">
            {banks.map((bank) => (
              <div
                key={bank.id}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {bank.file_url ? (
                    <img src={bank.file_url} alt={bank.bank_name} className="w-8 h-8 object-contain rounded" />
                  ) : (
                    <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">{bank.bank_name}</p>
                    <p className="text-xs text-gray-500">Code: {bank.code}</p>
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${bank.status === "ACTIVE" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}
                  >
                    {bank.status}
                  </span>
                </div>
                <div className="flex items-center space-x-1 ml-2">
                  <button
                    onClick={() => startEdit(bank)}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Modifier"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(bank.id!)}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500 py-8">Aucune banque pour ce transfert</p>
        )}
      </div>
    </div>
  );
};

// Modal de confirmation de suppression
const DeleteConfirmModal: React.FC<{
  transfert: CountryTransfert | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}> = ({ transfert, isOpen, onClose, onConfirm, isLoading }) => {
  if (!isOpen || !transfert) return null;

  const senderName = transfert.country_sender?.name || "?";
  const destName = transfert.country_destination?.name || "?";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center space-x-3 mb-6">
          <div className="flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800">Confirmer la suppression</h2>
        </div>

        <div className="mb-6">
          <p className="text-gray-600">
            Êtes-vous sûr de vouloir supprimer le transfert{" "}
            <strong>
              {senderName} → {destName}
            </strong>{" "}
            ?
          </p>
          <p className="text-sm text-gray-500 mt-2">Cette action est irréversible.</p>
        </div>

        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isLoading}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Suppression...</span>
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                <span>Supprimer</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal d'édition / création
const EditTransfertModal: React.FC<{
  transfert: CountryTransfert | null;
  countries: Country[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { country_sender_id: number; country_destination_id: number; rate: string }, id?: any) => void;
  isLoading: boolean;
  isCreating: boolean;
}> = ({ transfert, countries, isOpen, onClose, onSave, isLoading, isCreating }) => {
  const [form, setForm] = useState({ country_sender_id: 0, country_destination_id: 0, rate: "" });
  const [errors, setErrors] = useState<{ country_sender_id?: string; country_destination_id?: string; rate?: string }>(
    {}
  );

  React.useEffect(() => {
    if (isCreating) {
      setForm({ country_sender_id: 0, country_destination_id: 0, rate: "" });
    } else if (transfert) {
      setForm({
        country_sender_id: transfert.country_sender_id,
        country_destination_id: transfert.country_destination_id,
        rate: transfert.rate,
      });
    }
    setErrors({});
  }, [transfert, isCreating, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: { country_sender_id?: string; country_destination_id?: string; rate?: string } = {};
    if (!form.country_sender_id) {
      newErrors.country_sender_id = "Le pays expéditeur est requis";
    }
    if (!form.country_destination_id) {
      newErrors.country_destination_id = "Le pays de destination est requis";
    }
    if (
      form.country_sender_id &&
      form.country_destination_id &&
      form.country_sender_id === form.country_destination_id
    ) {
      newErrors.country_destination_id = "Le pays de destination doit être différent";
    }
    if (!form.rate.trim()) {
      newErrors.rate = "Le taux est requis";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(form, isCreating ? undefined : transfert?.id);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800">
            {isCreating ? "Ajouter un Transfert" : "Modifier le Transfert"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pays Expéditeur *</label>
            <select
              value={form.country_sender_id}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, country_sender_id: Number(e.target.value) }));
                if (errors.country_sender_id) setErrors((prev) => ({ ...prev, country_sender_id: undefined }));
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800 ${
                errors.country_sender_id ? "border-red-300" : "border-gray-300"
              }`}
              disabled={isLoading}
            >
              <option value={0}>-- Sélectionner un pays --</option>
              {countries.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.code}) - +{c.phonecode} - {c.devise}
                </option>
              ))}
            </select>
            {errors.country_sender_id && <p className="mt-1 text-sm text-red-600">{errors.country_sender_id}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pays de Destination *</label>
            <select
              value={form.country_destination_id}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, country_destination_id: Number(e.target.value) }));
                if (errors.country_destination_id)
                  setErrors((prev) => ({ ...prev, country_destination_id: undefined }));
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-800 ${
                errors.country_destination_id ? "border-red-300" : "border-gray-300"
              }`}
              disabled={isLoading}
            >
              <option value={0}>-- Sélectionner un pays --</option>
              {countries.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.code}) - +{c.phonecode} - {c.devise}
                </option>
              ))}
            </select>
            {errors.country_destination_id && (
              <p className="mt-1 text-sm text-red-600">{errors.country_destination_id}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Taux *</label>
            <input
              type="text"
              value={form.rate}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, rate: e.target.value }));
                if (errors.rate) setErrors((prev) => ({ ...prev, rate: undefined }));
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                errors.rate ? "border-red-300" : "border-gray-300"
              }`}
              placeholder="Ex: 1.5, 655.957..."
              disabled={isLoading}
            />
            {errors.rate && <p className="mt-1 text-sm text-red-600">{errors.rate}</p>}
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{isCreating ? "Création..." : "Modification..."}</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>{isCreating ? "Créer" : "Modifier"}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Country info display helper
const CountryInfo: React.FC<{ country?: Country }> = ({ country }) => {
  if (!country) return <span className="text-gray-400">—</span>;
  return (
    <div>
      <p className="font-medium text-gray-800">{country.name}</p>
      <p className="text-xs text-gray-500">
        {country.code} · +{country.phonecode} · {country.devise}
      </p>
    </div>
  );
};

// Main Page
const CountryTransfertsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<PageTab>("rates");

  const {
    countryTransferts,
    deleteCountryTransfert,
    updateCountryTransfert,
    createCountryTransfert,
    countryTransfertsLoading,
  } = useCountryTransferts({});
  const { countries } = useCountries({});
  console.log("countryTransferts", countryTransferts);

  const [filters, setFilters] = useState<any>({ search: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [editingTransfert, setEditingTransfert] = useState<CountryTransfert | null>(null);
  const [deletingTransfert, setDeletingTransfert] = useState<CountryTransfert | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [itemsTransfert, setItemsTransfert] = useState<CountryTransfert | null>(null);
  const [emailsTransfert, setEmailsTransfert] = useState<CountryTransfert | null>(null);
  const [operatorsTransfert, setOperatorsTransfert] = useState<CountryTransfert | null>(null);
  const [banksTransfert, setBanksTransfert] = useState<CountryTransfert | null>(null);
  const [transactionsTransfert, setTransactionsTransfert] = useState<CountryTransfert | null>(null);

  const itemsPerPage = 10;

  // Filter logic
  const filteredTransferts = (countryTransferts || []).filter((t) => {
    if (filters.search === "") return true;
    const s = filters.search.toLowerCase();
    return (
      (t.country_sender?.name || "").toLowerCase().includes(s) ||
      (t.country_sender?.code || "").toLowerCase().includes(s) ||
      (t.country_sender?.devise || "").toLowerCase().includes(s) ||
      (t.country_destination?.name || "").toLowerCase().includes(s) ||
      (t.country_destination?.code || "").toLowerCase().includes(s) ||
      (t.country_destination?.devise || "").toLowerCase().includes(s) ||
      t.rate.toLowerCase().includes(s)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredTransferts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredTransferts.length);
  const currentTransferts = filteredTransferts.slice(startIndex, endIndex);

  const handleAction = (action: string, transfert: CountryTransfert) => {
    switch (action) {
      case "edit":
        setIsCreating(false);
        setEditingTransfert(transfert);
        break;
      case "delete":
        setDeletingTransfert(transfert);
        break;
      case "items":
        setItemsTransfert(transfert);
        break;
      case "emails":
        setEmailsTransfert(transfert);
        break;
      case "operators":
        setOperatorsTransfert(transfert);
        break;
      case "banks":
        setBanksTransfert(transfert);
        break;
      case "transactions":
        setTransactionsTransfert(transfert);
        break;
    }
  };

  const handleSave = async (
    data: { country_sender_id: number; country_destination_id: number; rate: string },
    id?: any
  ) => {
    setIsEditLoading(true);
    try {
      if (isCreating) {
        await createCountryTransfert(data);
      } else {
        await updateCountryTransfert(id, data);
      }
      setEditingTransfert(null);
      setIsCreating(false);
    } catch (error) {
      console.error("Erreur:", error);
      alert(isCreating ? "Erreur lors de la création" : "Erreur lors de la modification");
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingTransfert) return;
    setIsDeleteLoading(true);
    try {
      await deleteCountryTransfert(deletingTransfert.id);
      setDeletingTransfert(null);
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      alert("Erreur lors de la suppression");
    } finally {
      setIsDeleteLoading(false);
    }
  };

  if (countryTransfertsLoading && activeTab === "rates") {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des transferts...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-2 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <ArrowRightLeft className="w-8 h-8 mt-2 text-green-600" />
              Transferts par Pays
            </h1>
            <p className="text-gray-600 mt-1">Gestion des taux et des corridors d'envoi entre pays</p>
          </div>
          <DownloadRatesPdfButton />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="flex border-b border-gray-200">
            <button
              type="button"
              onClick={() => setActiveTab("rates")}
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                activeTab === "rates"
                  ? "text-green-600 border-b-2 border-green-600"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              <ArrowRightLeft className="w-4 h-4" />
              Taux de transfert
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("corridors")}
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                activeTab === "corridors"
                  ? "text-green-600 border-b-2 border-green-600"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              <MapPin className="w-4 h-4" />
              Corridors d'envoi
            </button>
          </div>
        </div>

        {activeTab === "corridors" ? (
          <CountrySendersTab />
        ) : (
          <>
            {/* Stats */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Total des transferts</h3>
                  <p className="text-3xl font-bold text-green-600">{countryTransferts?.length || 0}</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Transferts filtrés</h3>
                  <p className="text-3xl font-bold text-blue-600">{filteredTransferts.length}</p>
                </div>
              </div>
            </div>

            {/* Search + Add */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
              <div className="flex justify-between items-center">
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher par pays, code, devise, taux..."
                      value={filters.search}
                      onChange={(e) => {
                        setFilters({ search: e.target.value });
                        setCurrentPage(1);
                      }}
                      className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>
                <button
                  onClick={() => {
                    setIsCreating(true);
                    setEditingTransfert({ country_sender_id: 0, country_destination_id: 0, rate: "" });
                  }}
                  className="ml-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Ajouter Transfert</span>
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Pays Expéditeur</th>
                      <th className="px-6 py-4 text-center text-sm font-medium text-gray-600"></th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Pays Destination</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Taux</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Éléments</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Emails</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Opérateurs</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Banques</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentTransferts.length > 0 ? (
                      currentTransferts.map((transfert) => (
                        <tr key={transfert.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <CountryInfo country={transfert.country_sender} />
                          </td>
                          <td className="px-6 py-4 text-center">
                            <ArrowRightLeft className="w-4 h-4 text-gray-400 mx-auto" />
                          </td>
                          <td className="px-6 py-4">
                            <CountryInfo country={transfert.country_destination} />
                          </td>
                          <td className="px-6 py-4">
                            <span className="font-semibold text-green-700">{transfert.rate}</span>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleAction("items", transfert)}
                              className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                              title="Gérer les éléments"
                            >
                              <Link2 className="w-4 h-4" />
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleAction("emails", transfert)}
                              className="p-1 text-gray-400 hover:text-sky-600 transition-colors"
                              title="Gérer les emails"
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleAction("operators", transfert)}
                              className="p-1 text-gray-400 hover:text-purple-600 transition-colors"
                              title="Gérer les opérateurs"
                            >
                              <Radio className="w-4 h-4" />
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleAction("banks", transfert)}
                              className="p-1 text-gray-400 hover:text-orange-600 transition-colors"
                              title="Gérer les banques"
                            >
                              <Building2 className="w-4 h-4" />
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleAction("transactions", transfert)}
                                className="p-1.5 text-blue-700 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
                                title="Voir les transactions du corridor"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleAction("edit", transfert)}
                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                title="Modifier"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleAction("delete", transfert)}
                                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={9} className="px-6 py-8 text-center text-gray-500">
                          Aucun transfert trouvé
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      Affichage {startIndex + 1}-{endIndex} sur {filteredTransferts.length} transferts
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        Précédent
                      </button>
                      <span className="text-sm text-gray-600">
                        Page {currentPage} sur {totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        Suivant
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modals */}
            <EditTransfertModal
              transfert={editingTransfert}
              countries={countries || []}
              isOpen={!!editingTransfert}
              onClose={() => {
                setEditingTransfert(null);
                setIsCreating(false);
              }}
              onSave={handleSave}
              isLoading={isEditLoading}
              isCreating={isCreating}
            />

            <DeleteConfirmModal
              transfert={deletingTransfert}
              isOpen={!!deletingTransfert}
              onClose={() => setDeletingTransfert(null)}
              onConfirm={handleDeleteConfirm}
              isLoading={isDeleteLoading}
            />

            <ItemsModal transfert={itemsTransfert} isOpen={!!itemsTransfert} onClose={() => setItemsTransfert(null)} />

            <EmailsModal
              transfert={emailsTransfert}
              isOpen={!!emailsTransfert}
              onClose={() => setEmailsTransfert(null)}
            />

            <OperatorsModal
              transfert={operatorsTransfert}
              isOpen={!!operatorsTransfert}
              onClose={() => setOperatorsTransfert(null)}
            />

            <BanksModal transfert={banksTransfert} isOpen={!!banksTransfert} onClose={() => setBanksTransfert(null)} />

            <CorridorTransactionsModal
              transfert={transactionsTransfert}
              isOpen={!!transactionsTransfert}
              onClose={() => setTransactionsTransfert(null)}
            />
          </>
        )}
      </div>
    </Layout>
  );
};

export default CountryTransfertsPage;
